import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { getStoredRef } from "../lib/referral";
import ListSignupModal from "../components/ListSignupModal";
import EventDetailModal from "../components/EventDetailModal";
import { Calendar, MapPin, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useContent, imgSrc, Slashed } from "../lib/content";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const { c } = useContent();
  useEffect(() => { api.get("/events").then(r => setEvents(r.data)); }, []);
  const openList = (ev) => {
    setDetail(null);
    if (!ev.list_url) { setActive(ev); return; }
    window.open(ev.list_url, "_blank", "noopener");
    api.post(`/events/${ev.id}/list-click`, { referral_code: getStoredRef() || undefined }).catch(() => {});
  };
  return (
    <div className="pb-16">
      {/* HERO */}
      <section className="relative h-[85vh] flex items-end overflow-hidden grid-lines">
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20"/>
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: c.home_hero_image ? `url(${imgSrc(c.home_hero_image)})` : "none",
          backgroundSize: "cover", backgroundPosition: "center"
        }}/>
        <div className="relative max-w-[1400px] mx-auto px-4 lg:px-8 pb-16 w-full">
          <div className="label-mono text-neon mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-neon rounded-full animate-pulse"/>
            <span data-testid="home-badge">{c.home_badge}</span>
          </div>
          <motion.h1 initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.6}}
            className="font-display text-5xl sm:text-7xl lg:text-9xl text-white leading-[0.85]" data-testid="home-title">
            <Slashed text={c.home_title_line1}/><br/>
            <span className="text-alert">{c.home_title_accent}</span> {c.home_title_line2}
          </motion.h1>
          <p className="mt-8 max-w-xl text-neutral-400 text-sm lg:text-base font-body" data-testid="home-subtitle">
            {c.home_subtitle} <span className="text-neon">{c.home_subtitle_highlight}</span>
          </p>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-neutral-800 py-4 bg-black overflow-hidden">
        <div className="marquee">
          <span className="marquee-track font-display text-3xl text-neutral-700 pr-20">
            {(c.home_marquee || "").repeat(6)}
          </span>
        </div>
      </div>

      {/* EVENTS GRID */}
      <section className="max-w-[1400px] mx-auto px-4 lg:px-8 pt-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono text-neon mb-2">{c.home_events_label}</div>
            <h2 className="font-display text-4xl lg:text-6xl">{c.home_events_title}</h2>
          </div>
          <div className="hidden md:block font-mono text-xs tracking-widest text-neutral-500">[{events.length} EVENTI]</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((ev, i) => (
            <motion.div key={ev.id} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
              transition={{delay: i*0.08, duration: 0.5}}
              className="card-dark group flex flex-col" data-testid={`event-card-${ev.id}`}>
              <button type="button" onClick={()=>setDetail(ev)} className="relative aspect-[4/5] overflow-hidden text-left cursor-pointer" data-testid={`event-poster-${ev.id}`} aria-label={`Dettagli ${ev.title}`}>
                <img src={imgSrc(ev.poster_url)} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700"/>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"/>
                <div className="absolute top-4 left-4 bg-black/80 backdrop-blur px-3 py-1 label-mono text-neon">
                  {new Date(ev.date).toLocaleDateString("it-IT",{day:"2-digit",month:"short"}).toUpperCase()}
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-display text-3xl text-white leading-none hover-glitch">{ev.title}</h3>
                  <div className="label-mono text-[9px] text-neon mt-2">TOCCA PER I DETTAGLI</div>
                </div>
              </button>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-xs text-neutral-400 font-mono tracking-wider mb-3">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{ev.venue}</span>
                  <span>·</span>
                  <span>{ev.city}</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-5">
                  {ev.lineup.slice(0,3).map(dj => (
                    <span key={dj} className="font-mono text-[10px] tracking-widest text-neutral-300 border border-neutral-800 px-2 py-1">{dj.toUpperCase()}</span>
                  ))}
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div>
                    <div className="label-mono text-[9px]">DA</div>
                    <div className="font-display text-xl text-neon">€{ev.price_from}</div>
                  </div>
                  <button onClick={()=>openList(ev)} className="btn-primary !py-2 !px-4 text-xs" data-testid={`list-btn-${ev.id}`}>
                    {c.home_list_button}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {active && <ListSignupModal event={active} onClose={()=>setActive(null)}/>}
      {detail && <EventDetailModal event={detail} onClose={()=>setDetail(null)} onList={openList}/>}
    </div>
  );
}
